import { useState, useRef, useEffect } from "react";

interface IntroAnimationProps {
  onComplete: () => void;
  onFadeStart?: () => void;
}

const LOADING_DURATION = 3500;
const DISPLAY_AFTER = 600;
const FADE_DURATION = 1000;

const imageUrl =
  "https://static.readdy.ai/image/413ccbf5fb2d9cba47392df0340806cc/c13c42def0257187c77bc5bdb647603c.png";

// Curva não linear com transições suaves entre segmentos
// Duração total: 3.5s
// Seg1: 0s→0.5s   (t: 0→0.143)      valor: 0→0.10  (muito lento, ease-in)
// Seg2: 0.5s→1.3s (t: 0.143→0.371)  valor: 0.10→0.30 (salta, smoothstep)
// Seg3: 1.3s→2.9s (t: 0.371→0.829)  valor: 0.30→0.50 (abranda, ease-out)
// Seg4: 2.9s→3.5s (t: 0.829→1.0)    valor: 0.50→1.0 (acelera, smoothstep)
// Usa smoothstep em todos os segmentos para transições orgânicas sem cortes
function smoothstep(s: number): number {
  return s * s * (3 - 2 * s);
}

function nonLinearProgress(t: number): number {
  // Segmento 1: 0 → 0.143 → 0 → 0.10 (muito lento, ease-in suave)
  if (t < 0.143) {
    const s = t / 0.143;
    return 0.10 * smoothstep(s * 0.7); // amortece o arranque
  }
  // Segmento 2: 0.143 → 0.371 → 0.10 → 0.30 (salta, smoothstep completo)
  if (t < 0.371) {
    const s = (t - 0.143) / 0.228;
    return 0.10 + 0.20 * smoothstep(s);
  }
  // Segmento 3: 0.371 → 0.829 → 0.30 → 0.50 (abranda, ease-out suave)
  if (t < 0.829) {
    const s = (t - 0.371) / 0.458;
    // ease-out: começa rápido (vindo do salto) e abranda gradualmente
    return 0.30 + 0.20 * (1 - (1 - s) * (1 - s));
  }
  // Segmento 4: 0.829 → 1.0 → 0.50 → 1.0 (acelera, smoothstep)
  const s = (t - 0.829) / 0.171;
  return 0.50 + 0.50 * smoothstep(s);
}

export default function IntroAnimation({ onComplete, onFadeStart }: IntroAnimationProps) {
  const [imageReady, setImageReady] = useState(false);
  const [revealPercent, setRevealPercent] = useState(0);
  const [fading, setFading] = useState(false);
  const fadingRef = useRef(false);
  const animFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const loadingDoneRef = useRef(false);

  const triggerFade = () => {
    if (!fadingRef.current) {
      fadingRef.current = true;
      setFading(true);
      onFadeStart?.();
    }
  };

  const handleTransitionEnd = () => {
    if (fadingRef.current) {
      onComplete();
    }
  };

  // Animação de loading
  useEffect(() => {
    if (!imageReady) return;

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }
      const elapsed = timestamp - startTimeRef.current;
      const t = Math.min(elapsed / LOADING_DURATION, 1);
      const progress = nonLinearProgress(t);
      setRevealPercent(progress * 100);

      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        loadingDoneRef.current = true;
        // Após loading completo, aguarda um momento e faz slide-up
        setTimeout(() => {
          triggerFade();
        }, DISPLAY_AFTER);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [imageReady]);

  // Opacidade da zona não revelada: 0.4
  // Opacidade da zona revelada: 1.0
  // Implementação: duas camadas sobrepostas
  // Camada base: imagem a 40% de opacidade (full width)
  // Camada reveal: imagem a 100% de opacidade, com clip-path da esquerda até revealPercent%

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black overflow-hidden"
      style={{
        transform: fading ? "translateY(-100%)" : "translateY(0)",
        transition: fading ? `transform ${FADE_DURATION}ms cubic-bezier(0.76, 0, 0.24, 1)` : "none",
        pointerEvents: fading ? "none" : "auto",
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      {/* Wrapper centrado */}
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Camada base: 40% de opacidade */}
        <img
          src={imageUrl}
          alt="ecos 3D"
          onLoad={() => setImageReady(true)}
          className="block w-[55vw] md:w-[40vw] max-w-[520px] h-auto"
          style={{
            opacity: imageReady ? 0.25 : 0,
            transition: imageReady ? "opacity 0.4s ease" : "none",
          }}
        />

        {/* Camada reveal: 100% de opacidade, revelada da esquerda para a direita */}
        {imageReady && (
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className="absolute block w-[55vw] md:w-[40vw] max-w-[520px] h-auto"
            style={{
              opacity: 1,
              clipPath: `inset(0 ${100 - revealPercent}% 0 0)`,
            }}
          />
        )}
      </div>
    </div>
  );
}
